import { useForm } from "react-hook-form";
import { useNavigate } from "react-router";

import { Flex, Heading, Input, Stack } from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";

import { patchHohishHohishesHohishUuidPatch } from "@/codegen/openapi2";
import { Button } from "@/codegen/ui/button";
import { NativeSelectField, NativeSelectRoot } from "@/codegen/ui/native-select";
import { toaster } from "@/codegen/ui/toaster";
import { FormStepCustom } from "@/components/2/form";
import { getEnrichedHohishKey } from "@/services/2/hohishes/queries";
import { getEnrichedHohishQueryFromNaive } from "@/services/2/hohishes/standalone-fetches";
import { queryClient } from "@/services/2/react-query";
import { useGetLegalEntityLegalEntitiesGet } from "@/services/2/react-query/hooks";

import { useEnrichedHohishOutletContext } from "../local-context";

const LUCCA_LEGAL_ENTITY = "__lucca_legal_entity__";

const HohishSchema = z.object({
  override_first_name: z.string(),
  override_last_name: z.string(),
  override_primary_mail_nickname: z.string(),
  legal_entity_selection: z.string().min(1),
  primary_mail_domain: z.string().min(1),
});

type HohishSchemaType = z.output<typeof HohishSchema>;

const uxWaiter = () => new Promise(resolve => setTimeout(resolve, 300));

export const EditIdentityHohish = () => {
  const hohish = useEnrichedHohishOutletContext();
  const navigate = useNavigate();
  const {
    data: legalEntities = [],
    isPending: areLegalEntitiesPending,
    isError: didLegalEntitiesFail,
  } = useGetLegalEntityLegalEntitiesGet({}, [], {
    staleTime: 12 * 60 * 60 * 1000,
  });
  const legalEntityItems = [
    { value: LUCCA_LEGAL_ENTITY, label: "Utiliser l'entité Lucca" },
    ...legalEntities
      .map(legalEntity => ({ value: legalEntity.uuid, label: legalEntity.name }))
      .sort((a, b) => a.label.localeCompare(b.label)),
  ];
  const primaryMailDomainItems = [
    ...new Set([
      hohish.primary_mail_domain,
      ...legalEntities.flatMap(legalEntity =>
        legalEntity.primary_mail_domain ? [legalEntity.primary_mail_domain] : []
      ),
    ]),
  ]
    .sort()
    .map(domain => ({ value: domain, label: domain }));
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<HohishSchemaType>({
    resolver: zodResolver(HohishSchema),
    defaultValues: {
      override_first_name: hohish.first_name,
      override_last_name: hohish.last_name,
      override_primary_mail_nickname: hohish.primary_mail_nickname,
      legal_entity_selection: hohish.legal_entity_uuid,
      primary_mail_domain: hohish.primary_mail_domain,
    },
  });

  if (areLegalEntitiesPending) {
    return <div>Chargement...</div>;
  }

  if (didLegalEntitiesFail) {
    return <div>Impossible de charger les entités légales.</div>;
  }

  return (
    <Stack gap={6} maxW={480}>
      <Heading size="xl">Modification de l'identité</Heading>
      <form
        onSubmit={handleSubmit(d => {
          const waiter = uxWaiter();
          const overrideLegalEntityUuid =
            d.legal_entity_selection === LUCCA_LEGAL_ENTITY
              ? null
              : d.legal_entity_selection === hohish.legal_entity_uuid
                ? undefined
                : d.legal_entity_selection;
          const primaryMailDomain =
            d.primary_mail_domain === hohish.primary_mail_domain
              ? undefined
              : d.primary_mail_domain;

          if (
            (overrideLegalEntityUuid !== undefined || primaryMailDomain !== undefined) &&
            !window.confirm(
              "Confirmer la modification de l'entité légale opérationnelle ou du domaine e-mail ?"
            )
          ) {
            return;
          }

          const body = {
            override_first_name: d.override_first_name,
            override_last_name: d.override_last_name,
            override_primary_mail_nickname: d.override_primary_mail_nickname,
            ...(overrideLegalEntityUuid !== undefined
              ? { override_legal_entity_uuid: overrideLegalEntityUuid }
              : {}),
            ...(primaryMailDomain !== undefined ? { primary_mail_domain: primaryMailDomain } : {}),
          };

          void patchHohishHohishesHohishUuidPatch({
            body,
            path: { hohish_uuid: hohish.uuid },
          }).then(async r => {
            if (r.error) {
              throw new Error("Error from server");
            }
            // Not too fast, to let the user see the loading state.
            await waiter;

            toaster.create({
              title: `Modification réussie`,
              description: "L'identité a bien été modifiée.",
              type: "success",
            });

            queryClient.setQueryData(
              getEnrichedHohishKey(hohish.uuid, ["uncached"]),
              await getEnrichedHohishQueryFromNaive(r.data)
            );

            void navigate("../identity");
          });
        })}
      >
        <Stack gap={6}>
          <FormStepCustom
            label="Prénom"
            input={<Input type="text" {...register("override_first_name")} />}
            error={errors.override_first_name}
          />

          <FormStepCustom
            label="Nom"
            input={<Input type="text" {...register("override_last_name")} />}
            error={errors.override_last_name}
          />

          <FormStepCustom
            label="Nickname"
            input={<Input type="text" {...register("override_primary_mail_nickname")} />}
            error={errors.override_primary_mail_nickname}
            helpText="Utilisé comme nom d'utilisateur dans les différents outils (adresses mail, Slack, etc.)."
          />

          <FormStepCustom
            label="Entité légale opérationnelle"
            input={
              <NativeSelectRoot>
                <NativeSelectField
                  items={legalEntityItems}
                  {...register("legal_entity_selection")}
                />
              </NativeSelectRoot>
            }
            error={errors.legal_entity_selection}
            helpText={`L'entité légale opérationnelle détermine notamment les permissions dans les différents outils. "Utiliser l'entité Lucca" supprime l'éventuelle surcharge opérationnelle.`}
          />

          <FormStepCustom
            label="Domaine mail"
            input={
              <NativeSelectRoot>
                <NativeSelectField
                  items={primaryMailDomainItems}
                  {...register("primary_mail_domain")}
                />
              </NativeSelectRoot>
            }
            error={errors.primary_mail_domain}
            helpText="Le domaine mail doit rester cohérent avec l'entité légale opérationnelle et doit être modifié explicitement."
          />

          <Flex justify="flex-end" gap={4}>
            <Button variant={"outline"} loading={isSubmitting} onClick={() => navigate(-1)}>
              Annuler
            </Button>

            <Button loading={isSubmitting} type="submit">
              Modifier l'identité
            </Button>
          </Flex>
        </Stack>
      </form>
    </Stack>
  );
};
